import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from 'helpers/trackerHelpers';
import _ from 'lodash';
import { pruneFilterLeaves } from 'modules/decision/pages/Explore/helpers/filterTree';
import { useExploreChartKey } from 'modules/decision/pages/Explore/hooks/chartKey';
import { ExploreActionType as EAT } from 'modules/decision/pages/Explore/hooks/reducers/exploreReducer';
import type { Dispatch, SetStateAction } from 'react';

import { useBackendSyncedContext } from '../../Contexts/BackendSyncedContext/BackendSyncedContext';
import { useExploreContext } from '../../Contexts/ExploreContext';
import { useSettingsDispatchContext } from '../../Contexts/SettingsContext/SettingsContext';
import {
    checkNullValueWithInvalidOperation,
    checkQuery,
    sentenceToExplore,
} from '../helpers';
import type { SentenceError } from './errors';

const hasExploreFieldsChanged = (
    prevExplore: ExploreQueryT,
    newExplore: ExploreQueryT,
) => {
    const prevMetrics = prevExplore.metrics.map((m) => m.field);
    const prevDimensions = prevExplore.dimensions.map(
        (d) => `${d.field}-${d.scale}`,
    );
    const newMetrics = newExplore.metrics.map((m) => m.field);
    const newDimensions = newExplore.dimensions.map(
        (d) => `${d.field}-${d.scale}`,
    );

    return (
        !_.isEqual(prevMetrics, newMetrics) ||
        !_.isEqual(prevDimensions, newDimensions)
    );
};

export const useTriggerQuery = (
    setQueryErrors: Dispatch<SetStateAction<SentenceError>> = () => {},
) => {
    const { explore, dispatchExplore } = useExploreContext();
    const { applyChartSettings } = useSettingsDispatchContext();
    const { setIsBackendSynced } = useBackendSyncedContext();
    const chartKey = useExploreChartKey();
    const queryClient = useQueryClient();

    return (query: SentenceQueryT) => {
        const { metrics, dimensions, filters } = query;
        // We add the aggregation to the metric since there is only one aggregation SentenceChunk
        // If there are no aggregation in metrics, we use the state aggregation.
        // It only happens when the user manually enters the request with Sentence.
        const sentenceQuery: SentenceQueryT = {
            metrics: metrics.filter((m) => m.field),
            dimensions: dimensions.filter((d) => d.field),
            filters: pruneFilterLeaves(
                filters,
                (f) =>
                    !!f.field &&
                    // TODO: improve this fix
                    /* under some conditions, it is possible to trigger the query with a filter 'in_' null or 'not_in' null.
                     The error handling is currently pretty bad and should be improved by the end of 2023. Until then, we filter out these cases
                     so that the UI is not stuck in loading mode. The filter won't be persisted in URL, won't be taken into account by the backend, but will
                     still be displayed in the Sentence so the user can still edit it or remove it. A better solution would be to prevent triggering the query
                     when invalid fiels are in the Sentence, and warn the user why */
                    !checkNullValueWithInvalidOperation(f.value, f.operation),
            ),
        };

        const errors = checkQuery(sentenceQuery);
        const exploreQuery = sentenceToExplore(sentenceQuery);
        trackEvent('explore/graph-generated-or-refreshed');
        if (
            _.map(
                errors,
                (array) =>
                    (Array.isArray(array) && array.length === 0) ||
                    Object.keys(array).length === 0,
            ).every((l) => l)
        ) {
            // If the request changes, when re-initialze the chart conception settings
            // because it can be outdated
            if (hasExploreFieldsChanged(explore, exploreQuery)) {
                applyChartSettings({ chartConception: undefined });
            }
            // The chart now shows a query the saved one no longer matches, so it has to be read
            // from the explore endpoint instead of the dashboard one
            if (chartKey) {
                setIsBackendSynced(false);
                queryClient.removeQueries({ queryKey: ['chart', chartKey] });
            }
            dispatchExplore({ type: EAT.setExplore, query: exploreQuery });
        } else setQueryErrors(errors);
    };
};
